import React from 'react';
import { useTranslation } from 'react-i18next';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useTheme } from '@mui/material';
import { QInputForm, QSpinner } from '@kubiq-base/core';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAppContext } from '../../state/AppContext';
import { useCache } from '../../state/CachingContext';
import { useApi } from '../../state/ApiContext';
import { IIDProvider, IAuthenticate } from '../../interfaces/';
import CenteredQButton from '../Shared/CenteredQButton';
import MessageBox from '../Shared/MessageBoxes/MessageBox';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import FlexFormContainer from '../Shared/FlexContainers/FlexFormContainer';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';
import ApiHelper from '../../helpers/ApiHelper';
import SiteHelper from '../../helpers/SiteHelper';

interface Props {
  email: string;
  idProviders?: IIDProvider[];
  reset: () => void;
}

const ProviderPasswordForm = ({ email, idProviders, reset }: Props): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation(TranslationNamespaces.login);
  const { postRequest } = useApi();
  const { state, setTokens, clearTokens } = useAppContext();
  const { getCache, clearCache, setCache } = useCache();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ password: string }>();

  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [loading, setLoading] = React.useState(false);
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const navigate = useNavigate();
  const session = getCache();

  const validatePassword = (value: string): string | undefined => {
    setErrorMessage(undefined);
    let error;

    if (value === '') error = 'requiredField';

    return error;
  };

  const onSubmit = async (data: { password: string }): Promise<void> => {
    if (data.password) {
      await postRequest<{ data: IAuthenticate }>(
        'Login/Authenticate',
        { email: email, password: data.password, accountType: 'web' },
        setLoading,
        ['invalidUser'],
      )
        .then((result) => {
          if (result.data) {
            const pathname = result.data.twoFactorEnabled ? '/Auth/2FA' : '/Enable2FA';
            setTokens(result.data);
            navigate(pathname);
          }
        })
        .catch(() => setErrorMessage('wrongPassword'));
    }
  };

  const reorderProviders = (providers: IIDProvider[]): IIDProvider[] => {
    const newProviders = SiteHelper.deepCopy(providers);

    newProviders.push(
      newProviders.splice(
        newProviders.findIndex((i) => i.identityProviderName === 'AtiQx.ID'),
        1,
      )[0],
    );

    return newProviders;
  };

  return (
    <>
      {isRedirecting ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72 }}>
          <QSpinner />
        </div>
      ) : (
        <>
          <HeaderWithDescription bigHeader translationNamespace={TranslationNamespaces.login}>
            <MessageBox text={email} emailBox />
          </HeaderWithDescription>

          {idProviders && idProviders.length > 0 && <div style={{ textAlign: 'center' }}>{t('idProvider:header')}</div>}

          {idProviders && (
            <div style={{ marginTop: theme.spacing(-1.5) }}>
              <FlexContainer>
                {reorderProviders(idProviders).map((result: IIDProvider) => {
                  const isAID = result.identityProviderName === 'AtiQx.ID';

                  return (
                    <div key={result.identityProviderName}>
                      <div
                        style={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(1.5), textAlign: 'center' }}
                      >
                        {isAID && t('or')}
                      </div>
                      {!isAID ? (
                        <CenteredQButton
                          color='primary'
                          fullWidth
                          onClick={() => ApiHelper.redirect(result.redirectUrl as string)}
                          disabled={!result.redirectUrl}
                        >
                          {result.identityProviderName}
                        </CenteredQButton>
                      ) : (
                        state.refreshToken &&
                        SiteHelper.parseJwt(state.accessToken).amr === 'mfa' && (
                          <CenteredQButton
                            color='secondary'
                            fullWidth
                            onClick={() => {
                              setIsRedirecting(true);
                              SiteHelper.refreshToken(state, session, setTokens, clearCache, postRequest, () => {
                                clearTokens();
                                setIsRedirecting(false);
                              });
                            }}
                          >
                            {`${t('continueWith')} ${result.identityProviderName}`}
                          </CenteredQButton>
                        )
                      )}
                    </div>
                  );
                })}
              </FlexContainer>
            </div>
          )}

          {(!state.refreshToken || !state.accessToken || SiteHelper.parseJwt(state.accessToken).amr !== 'mfa') && (
            <div {...(idProviders && idProviders.length > 1 && { style: { marginTop: theme.spacing(-5.5) } })}>
              <FlexFormContainer onSubmit={handleSubmit(onSubmit)}>
                <QInputForm
                  control={control}
                  name='password'
                  label={t('password')}
                  type='password'
                  showPasswordToggle
                  autoFocus={true}
                  rules={{ validate: validatePassword }}
                  isError={
                    (errors.password && t(errors.password?.message as string)) || (errorMessage && t(errorMessage))
                  }
                />

                <CenteredQButton type='submit' isLoading={loading} fullWidth color='secondary'>
                  {idProviders && idProviders.length > 1 ? t('nextAID') : t('next')}
                </CenteredQButton>
              </FlexFormContainer>
            </div>
          )}

          <FlexContainer>
            {(!state.refreshToken || !state.accessToken || SiteHelper.parseJwt(state.accessToken).amr !== 'mfa') && (
              <CenteredQButton
                to={session.verifiedPhone ? '/Recovery/Password' : '/Recovery/NotVerified'}
                variant='text'
              >
                {t('forgotPassword')}
              </CenteredQButton>
            )}
            <CenteredQButton variant='text' onClick={reset}>
              {t('cancel')}
            </CenteredQButton>
          </FlexContainer>
        </>
      )}
    </>
  );
};

export default ProviderPasswordForm;
